import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, ShieldCheck, CheckCircle2, AlertCircle, RefreshCw, Users as UsersIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

const Pill = ({ children, tone = "neutral", testid }) => {
  const map = {
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    warn: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    neutral: "bg-white/5 text-spike-dim border-white/10",
    admin: "bg-spike-blue/15 text-spike-blue-light border-spike-blue/30",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[0.65rem] font-orbitron uppercase tracking-[0.15em] border ${map[tone]}`}>
      {children}
    </span>
  );
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyUserId, setBusyUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { withCredentials: true });
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resyncShopify = async (userId) => {
    setBusyUserId(userId);
    try {
      await api.post(`/admin/shopify/sync-user/${userId}`, {}, { withCredentials: true });
      toast.success("Shopify sync queued");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusyUserId(null);
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      [u.name, u.email, u.phone, u.user_id]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [q, users]);

  const stats = useMemo(() => ({
    total: users.length,
    waiver_signed: users.filter((u) => !!u.waiver_signed_at).length,
    shopify_synced: users.filter((u) => !!u.shopify_customer_id).length,
    admins: users.filter((u) => u.is_admin).length,
  }), [users]);

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-orbitron text-2xl font-black uppercase text-spike-text inline-flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-spike-blue-light"/> Users
          </h1>
          <p className="text-sm text-spike-text-muted mt-1">All registered Spike Sports accounts, newest first.</p>
        </div>
        <button type="button" onClick={load} data-testid="users-refresh-btn" className="inline-flex items-center gap-2 text-xs text-spike-text hover:text-spike-blue-light border border-white/15 hover:border-spike-blue/40 px-3 py-2 rounded-sm">
          <RefreshCw className="w-3 h-3"/> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-spike-elevated border border-white/10 rounded-md p-4" data-testid="stat-total">
          <div className="text-[0.65rem] font-orbitron uppercase tracking-[0.25em] text-spike-dim">Total accounts</div>
          <div className="mt-1 font-orbitron text-2xl font-black text-spike-text">{stats.total}</div>
        </div>
        <div className="bg-spike-elevated border border-white/10 rounded-md p-4" data-testid="stat-waiver">
          <div className="text-[0.65rem] font-orbitron uppercase tracking-[0.25em] text-spike-dim">Waiver on file</div>
          <div className="mt-1 font-orbitron text-2xl font-black text-emerald-400">{stats.waiver_signed}</div>
        </div>
        <div className="bg-spike-elevated border border-white/10 rounded-md p-4" data-testid="stat-shopify">
          <div className="text-[0.65rem] font-orbitron uppercase tracking-[0.25em] text-spike-dim">Synced to Shopify</div>
          <div className="mt-1 font-orbitron text-2xl font-black text-spike-blue-light">{stats.shopify_synced}</div>
        </div>
        <div className="bg-spike-elevated border border-white/10 rounded-md p-4" data-testid="stat-admins">
          <div className="text-[0.65rem] font-orbitron uppercase tracking-[0.25em] text-spike-dim">Admins</div>
          <div className="mt-1 font-orbitron text-2xl font-black text-amber-300">{stats.admins}</div>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim" />
        <input
          type="search"
          data-testid="users-search-input"
          placeholder="Search by name, email or phone..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-spike-dim p-6"><Loader2 className="w-4 h-4 animate-spin"/> Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-spike-elevated border border-white/10 rounded-lg p-10 text-center text-spike-text-muted">No users found.</div>
      ) : (
        <div className="overflow-x-auto bg-spike-elevated border border-white/10 rounded-lg">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-black/40 text-spike-dim text-xs font-orbitron uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Auth</th>
                <th className="text-left px-4 py-3">Waiver</th>
                <th className="text-left px-4 py-3">Shopify</th>
                <th className="text-left px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((u) => {
                const methods = u.auth_methods || [];
                const synced = !!u.shopify_customer_id;
                return (
                  <tr key={u.user_id} className="hover:bg-black/40" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3 text-spike-text-muted whitespace-nowrap text-xs">{fmt(u.created_at)}</td>
                    <td className="px-4 py-3 text-spike-text">
                      <div className="font-semibold">{u.name || "—"}</div>
                      {u.is_admin && <Pill tone="admin" testid={`user-${u.user_id}-admin-pill`}><ShieldCheck className="w-3 h-3"/> Admin</Pill>}
                    </td>
                    <td className="px-4 py-3 text-spike-text-muted">{u.email}</td>
                    <td className="px-4 py-3 text-spike-text-muted">{u.phone || <span className="text-amber-300">missing</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {methods.includes("password") && <Pill>email</Pill>}
                        {methods.includes("google") && <Pill>google</Pill>}
                        {methods.length === 0 && <span className="text-spike-dim text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.waiver_signed_at
                        ? <Pill tone="ok" testid={`user-${u.user_id}-waiver-ok`}><CheckCircle2 className="w-3 h-3"/> Signed</Pill>
                        : <Pill tone="warn"><AlertCircle className="w-3 h-3"/> Unsigned</Pill>}
                    </td>
                    <td className="px-4 py-3">
                      {synced
                        ? <Pill tone="ok" testid={`user-${u.user_id}-shopify-ok`}><CheckCircle2 className="w-3 h-3"/> {u.shopify_phone_skipped ? "ok · no phone" : "synced"}</Pill>
                        : <Pill tone="warn" testid={`user-${u.user_id}-shopify-missing`}><AlertCircle className="w-3 h-3"/> Not synced</Pill>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => resyncShopify(u.user_id)}
                        disabled={busyUserId === u.user_id}
                        data-testid={`user-${u.user_id}-resync-btn`}
                        className="text-xs text-spike-blue-light hover:underline disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        <RefreshCw className={`w-3 h-3 ${busyUserId === u.user_id ? "animate-spin" : ""}`}/>
                        {synced ? "Re-sync Shopify" : "Sync to Shopify"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
